"use client";

import {
  useCallback,
  useEffect,
  useLayoutEffect,
  useRef,
  type Dispatch,
  type MutableRefObject,
  type SetStateAction,
} from "react";
import type { ProductSummary } from "@cabal/shared/domain/session/product/types.js";
import { MarkReadType } from "@cabal/shared/domain/session/product/enums.js";
import type { VersionSummary } from "@cabal/shared/domain/session/version/types.js";
import { useApi } from "@/contexts/api";
import type {
  LoadingStates,
  ProductsSeenMap,
} from "@/types/frontend-types";
import type { SessionCollab } from "./context";
import {
  mergeLatestDate,
  parseDateOrNull,
} from "./session-date-selectors";
import {
  mergeVersionMap,
  toProductMap,
  toSessionCollab,
} from "./session-data-provider-utils";
import { selectSessionVersions } from "./hook-selectors";
import { selectRefreshedProductsSeen } from "./read-tracking-selectors";

type ApiClient = ReturnType<typeof useApi>;

interface UseSessionProductActionsInput {
  api: ApiClient;
  sessionId: string;
  products: Record<string, ProductSummary>;
  versions: Record<string, VersionSummary>;
  productsSeen: ProductsSeenMap;
  focusedProductId: string | null;
  focusedProductUpdatedAt: string | undefined;
  rootProductIdRef: MutableRefObject<string | null>;
  collabs: Record<string, SessionCollab>;
  collabsByProduct: Record<string, string[]>;
  setLoading: Dispatch<SetStateAction<LoadingStates>>;
  setProducts: Dispatch<SetStateAction<Record<string, ProductSummary>>>;
  setVersions: Dispatch<SetStateAction<Record<string, VersionSummary>>>;
  replaceProductCollabItems: (input: {
    productId: string;
    items: readonly SessionCollab[];
  }) => void;
  tagOwnCollabsAndAdvanceRead: (input: {
    productId: string | null;
    collabs: readonly SessionCollab[];
  }) => void;
  updateLocalProductReadMarker: (input: {
    productId: string | null;
    collabsReadAt?: Date | null;
    productSeenAt?: Date | null;
  }) => void;
  replaceProductsSeen: (input: { productsSeen: ProductsSeenMap }) => void;
  updateProductsSinceTimestamp: (productsSince: string) => void;
  updateProductsSeen: (input: {
    productId: string;
    patch: { lastAddedVersion?: Date | null };
  }) => void;
}

interface RefreshedProductsEnvelope {
  items: readonly ProductSummary[];
  now: string;
}

interface SetProductsLoadingInput {
  silent: boolean;
  loading: boolean;
  setLoading: Dispatch<SetStateAction<LoadingStates>>;
}

interface ApplyRefreshedProductsInput {
  envelope: RefreshedProductsEnvelope;
  productsSeen: ProductsSeenMap;
  setProducts: Dispatch<SetStateAction<Record<string, ProductSummary>>>;
  replaceProductsSeen: (input: { productsSeen: ProductsSeenMap }) => void;
  updateProductsSinceTimestamp: (productsSince: string) => void;
}

interface MarkProductReadActionInput {
  api: ApiClient;
  sessionId: string;
  updateLocalProductReadMarker: UseSessionProductActionsInput["updateLocalProductReadMarker"];
}

interface EnsureProductVersionsActionInput {
  api: ApiClient;
  sessionId: string;
  products: Record<string, ProductSummary>;
  versions: Record<string, VersionSummary>;
  setVersions: Dispatch<SetStateAction<Record<string, VersionSummary>>>;
  updateProductsSeen: UseSessionProductActionsInput["updateProductsSeen"];
}

interface ApplyFetchedVersionsInput {
  productId: string;
  versions: VersionSummary[];
  setVersions: Dispatch<SetStateAction<Record<string, VersionSummary>>>;
  updateProductsSeen: UseSessionProductActionsInput["updateProductsSeen"];
}

interface EnsureProductCollabsActionInput {
  api: ApiClient;
  sessionId: string;
  collabs: Record<string, SessionCollab>;
  collabsByProduct: Record<string, string[]>;
  replaceProductCollabItems: UseSessionProductActionsInput["replaceProductCollabItems"];
  tagOwnCollabsAndAdvanceRead: UseSessionProductActionsInput["tagOwnCollabsAndAdvanceRead"];
}

interface RefreshProductsActionInput {
  api: ApiClient;
  sessionId: string;
  productsSeen: ProductsSeenMap;
  setLoading: Dispatch<SetStateAction<LoadingStates>>;
  setProducts: Dispatch<SetStateAction<Record<string, ProductSummary>>>;
  replaceProductsSeen: UseSessionProductActionsInput["replaceProductsSeen"];
  updateProductsSinceTimestamp: UseSessionProductActionsInput["updateProductsSinceTimestamp"];
}

interface FocusedProductReadRefreshInput {
  focusedProductId: string | null;
  focusedProductUpdatedAt: string | undefined;
  rootProductIdRef: MutableRefObject<string | null>;
  markProductRead: SessionProductActions["markProductRead"];
  refreshProducts: SessionProductActions["refreshProducts"];
}

export interface SessionProductActions {
  markProductRead: (productId: string) => Promise<void>;
  ensureProductVersions: (
    productId: string,
    limit?: number,
  ) => Promise<VersionSummary[]>;
  ensureProductCollabs: (
    productId: string,
    limit?: number,
  ) => Promise<SessionCollab[]>;
  refreshProducts: (options?: { silent?: boolean }) => Promise<void>;
}

export function useSessionProductActions(
  input: UseSessionProductActionsInput,
): SessionProductActions {
  const markProductRead = useMarkProductReadAction({
    api: input.api,
    sessionId: input.sessionId,
    updateLocalProductReadMarker: input.updateLocalProductReadMarker,
  });
  const ensureProductVersions = useEnsureProductVersionsAction({
    api: input.api,
    sessionId: input.sessionId,
    products: input.products,
    versions: input.versions,
    setVersions: input.setVersions,
    updateProductsSeen: input.updateProductsSeen,
  });
  const ensureProductCollabs = useEnsureProductCollabsAction({
    api: input.api,
    sessionId: input.sessionId,
    collabs: input.collabs,
    collabsByProduct: input.collabsByProduct,
    replaceProductCollabItems: input.replaceProductCollabItems,
    tagOwnCollabsAndAdvanceRead: input.tagOwnCollabsAndAdvanceRead,
  });
  const refreshProducts = useRefreshProductsAction({
    api: input.api,
    sessionId: input.sessionId,
    productsSeen: input.productsSeen,
    setLoading: input.setLoading,
    setProducts: input.setProducts,
    replaceProductsSeen: input.replaceProductsSeen,
    updateProductsSinceTimestamp: input.updateProductsSinceTimestamp,
  });

  useFocusedProductReadRefresh({
    focusedProductId: input.focusedProductId,
    focusedProductUpdatedAt: input.focusedProductUpdatedAt,
    rootProductIdRef: input.rootProductIdRef,
    markProductRead,
    refreshProducts,
  });

  return {
    markProductRead,
    ensureProductVersions,
    ensureProductCollabs,
    refreshProducts,
  };
}

function useMarkProductReadAction({
  api,
  sessionId,
  updateLocalProductReadMarker,
}: MarkProductReadActionInput): SessionProductActions["markProductRead"] {
  return useCallback(
    async (productId: string): Promise<void> => {
      try {
        await api.sessions
          .byId(sessionId)
          .products.byId(productId)
          .markRead({ body: { mark: [MarkReadType.Product] } });
        updateLocalProductReadMarker({
          productId,
          productSeenAt: new Date(),
        });
      } catch (error) {}
    },
    [api, sessionId, updateLocalProductReadMarker],
  );
}

function useEnsureProductVersionsAction({
  api,
  sessionId,
  products,
  versions,
  setVersions,
  updateProductsSeen,
}: EnsureProductVersionsActionInput): SessionProductActions["ensureProductVersions"] {
  return useCallback(
    async (productId: string, limit = 50): Promise<VersionSummary[]> => {
      const product = products[productId];
      const {
        versions: cachedVersions,
        hasCompleteContextVersions,
      } = selectSessionVersions({
        product,
        versionsById: versions,
      });

      if (hasCompleteContextVersions) {
        return cachedVersions;
      }

      const fetchedVersions = await fetchProductVersions({
        api,
        sessionId,
        productId,
        limit,
      });
      applyFetchedVersions({
        productId,
        versions: fetchedVersions,
        setVersions,
        updateProductsSeen,
      });
      return fetchedVersions;
    },
    [api, products, sessionId, setVersions, updateProductsSeen, versions],
  );
}

function useEnsureProductCollabsAction({
  api,
  sessionId,
  collabs,
  collabsByProduct,
  replaceProductCollabItems,
  tagOwnCollabsAndAdvanceRead,
}: EnsureProductCollabsActionInput): SessionProductActions["ensureProductCollabs"] {
  return useCallback(
    async (productId: string, limit = 50): Promise<SessionCollab[]> => {
      const cachedCollabs = selectCachedProductCollabs({
        productId,
        collabs,
        collabsByProduct,
      });
      if (cachedCollabs) return cachedCollabs;

      try {
        const items = await fetchProductCollabs({
          api,
          sessionId,
          productId,
          limit,
        });
        replaceProductCollabItems({
          productId,
          items,
        });
        tagOwnCollabsAndAdvanceRead({ productId, collabs: items });
        return items;
      } catch (error) {
        return [];
      }
    },
    [
      api,
      collabs,
      collabsByProduct,
      replaceProductCollabItems,
      sessionId,
      tagOwnCollabsAndAdvanceRead,
    ],
  );
}

function useFocusedProductReadRefresh({
  focusedProductId,
  focusedProductUpdatedAt,
  rootProductIdRef,
  markProductRead,
  refreshProducts,
}: FocusedProductReadRefreshInput): void {
  const markProductReadRef = useRef(markProductRead);
  const refreshProductsRef = useRef(refreshProducts);
  useLayoutEffect(() => {
    markProductReadRef.current = markProductRead;
    refreshProductsRef.current = refreshProducts;
  }, [markProductRead, refreshProducts]);

  useEffect(() => {
    if (!focusedProductId || focusedProductId === rootProductIdRef.current) {
      return;
    }
    void (async () => {
      await markProductReadRef.current(focusedProductId);
      await refreshProductsRef.current({ silent: true });
    })();
  }, [focusedProductId, focusedProductUpdatedAt, rootProductIdRef]);
}

function useRefreshProductsAction({
  api,
  sessionId,
  productsSeen,
  setLoading,
  setProducts,
  replaceProductsSeen,
  updateProductsSinceTimestamp,
}: RefreshProductsActionInput): SessionProductActions["refreshProducts"] {
  return useCallback(
    async (options?: { silent?: boolean }): Promise<void> => {
      const silent = options?.silent ?? false;
      try {
        setProductsLoading({ silent, loading: true, setLoading });

        const envelope = await fetchProductsList({ api, sessionId });

        applyRefreshedProducts({
          envelope,
          productsSeen,
          setProducts,
          replaceProductsSeen,
          updateProductsSinceTimestamp,
        });
        setProductsLoading({ silent, loading: false, setLoading });
      } catch (error) {
        setProductsLoading({ silent, loading: false, setLoading });
      }
    },
    [
      api,
      productsSeen,
      replaceProductsSeen,
      sessionId,
      setLoading,
      setProducts,
      updateProductsSinceTimestamp,
    ],
  );
}

async function fetchProductsList(input: {
  api: ApiClient;
  sessionId: string;
}): Promise<RefreshedProductsEnvelope> {
  return input.api.sessions.byId(input.sessionId).products.list();
}

async function fetchProductVersions(input: {
  api: ApiClient;
  sessionId: string;
  productId: string;
  limit: number;
}): Promise<VersionSummary[]> {
  const envelope = await input.api.sessions
    .byId(input.sessionId)
    .products.byId(input.productId)
    .versions({
      query: { limit: input.limit },
    });

  return envelope.items;
}

function applyFetchedVersions({
  productId,
  versions,
  setVersions,
  updateProductsSeen,
}: ApplyFetchedVersionsInput): void {
  setVersions((prev) =>
    mergeVersionMap({
      previous: prev,
      incoming: versions,
    }),
  );
  updateProductsSeen({
    productId,
    patch: {
      lastAddedVersion: selectLatestVersionCreatedAt({ versions }),
    },
  });
}

function selectLatestVersionCreatedAt(input: {
  versions: readonly VersionSummary[];
}): Date | null {
  return input.versions.reduce<Date | null>(
    (latest, version) =>
      mergeLatestDate({
        existing: latest,
        incoming: parseDateOrNull(version.createdAt),
      }),
    null,
  );
}

function selectCachedProductCollabs(input: {
  productId: string;
  collabs: Record<string, SessionCollab>;
  collabsByProduct: Record<string, string[]>;
}): SessionCollab[] | null {
  const hasLoadedCollabs = Object.prototype.hasOwnProperty.call(
    input.collabsByProduct,
    input.productId,
  );
  if (!hasLoadedCollabs) return null;

  return (input.collabsByProduct[input.productId] ?? [])
    .map((collabId) => input.collabs[collabId])
    .filter((collab): collab is SessionCollab => Boolean(collab));
}

async function fetchProductCollabs(input: {
  api: ApiClient;
  sessionId: string;
  productId: string;
  limit: number;
}): Promise<SessionCollab[]> {
  const response = await input.api.sessions.byId(input.sessionId).collabs({
    query: {
      productId: input.productId,
      limit: input.limit,
    },
  });

  return response.items.map((collab) => toSessionCollab({ collab }));
}

function applyRefreshedProducts({
  envelope,
  productsSeen,
  setProducts,
  replaceProductsSeen,
  updateProductsSinceTimestamp,
}: ApplyRefreshedProductsInput): void {
  setProducts(toProductMap(envelope.items));
  replaceProductsSeen({
    productsSeen: selectRefreshedProductsSeen({
      products: envelope.items,
      previous: productsSeen,
    }),
  });
  updateProductsSinceTimestamp(envelope.now);
}

function setProductsLoading({
  silent,
  loading,
  setLoading,
}: SetProductsLoadingInput): void {
  if (silent) return;

  setLoading((prev) => ({ ...prev, products: loading }));
}
